import { Dialog } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FoodDetailItem, FoodItem, listRestaurantType } from '../../../../../interfaces/types';
import { formatPriceItem } from '../../../../../utils';
import { isPreOrderDisabled } from '../../../../../utils/preOrderDisabled';
import { GameDayData } from '../../../../../utils/timeSlots';

interface RecommendedFoodDialogProps {
    open: boolean;
    onClose: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
    recommendedFoods: FoodDetailItem[];
    onFoodClick?: (food: FoodDetailItem) => void;
    onQuickAdd?: (food: FoodDetailItem) => boolean;
    orderDetails?: { foods: FoodItem[] };
    masterQuantities?: { [key: string]: number };
    onGoToCart?: () => void;
    listRestaurantData?: listRestaurantType[];
    orderDate?: string | null;
    isPreOrder?: boolean;
    deliveryRequest?: string;
    gameDayData?: GameDayData | null;
}

const RecommendedFoodDialog: React.FC<RecommendedFoodDialogProps> = ({
    open,
    onClose,
    recommendedFoods,
    onFoodClick,
    onQuickAdd,
    orderDetails,
    masterQuantities,
    onGoToCart,
    listRestaurantData,
    orderDate,
    isPreOrder = false,
    deliveryRequest,
    gameDayData,
}) => {
    const { t } = useTranslation();

    const hasAnyQuantity = masterQuantities
        ? recommendedFoods.some((food) => {
            const masterId = food.food_id || food.id;
            return (masterQuantities[masterId] || 0) > 0;
        })
        : false;

    const handleAdd = (food: FoodDetailItem) => {
        if (onQuickAdd) {
            onQuickAdd(food);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    maxWidth: '400px',
                    margin: '16px',
                    height: '600px',
                    maxHeight: '90vh',
                },
            }}
        >
            <div className="bg-white rounded-xl h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">{t('THE_SPECIALS')}</h2>
                    <button onClick={() => onClose({}, 'escapeKeyDown')} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Food Grid */}
                <div className="p-4 flex-1 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                        {recommendedFoods.map((food) => {
                            const masterId = food.food_id || food.id;
                            const sessionCount = masterQuantities ? (masterQuantities[masterId] || 0) : 0;
                            const isAdded = sessionCount > 0;
                            const restaurant = listRestaurantData?.find((r) => r.id === food.restaurantId);
                            const preOrderStatus = isPreOrderDisabled(food, restaurant, orderDate, isPreOrder, deliveryRequest, gameDayData);
                            const isDisabled = isPreOrder ? preOrderStatus.disabled : !food.enabled;

                            return (
                                <div
                                    key={food.id}
                                    className={`flex flex-col w-[140px] transition-shadow ${isDisabled ? 'opacity-60' : ''}`}
                                >
                                    {/* Food Image */}
                                    <div className="relative w-[140px] h-[140px] bg-gray-100">
                                        {food.image_url && (
                                            <img src={food.image_url} alt={food.name} className="absolute top-0 left-0 w-full h-full object-cover rounded-lg" />
                                        )}
                                    </div>

                                    {/* Food Info */}
                                    <div className="pt-2 flex-1 flex flex-col">
                                        <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">{food.name}</p>
                                        <div className="flex items-center justify-between mt-auto">
                                            <p className="text-sm font-semibold text-green-600">¥{formatPriceItem(food.price)}</p>
                                            <div className="flex items-center gap-2">
                                                {isAdded && <span className="text-sm font-bold text-green-600">{sessionCount}</span>}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isDisabled) return;
                                                        handleAdd(food);
                                                    }}
                                                    disabled={isDisabled}
                                                    className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${isAdded ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 hover:bg-gray-400'
                                                        } disabled:bg-gray-200 disabled:cursor-not-allowed`}
                                                >
                                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="p-4 border-t flex-shrink-0">
                    <button
                        onClick={() => {
                            if (hasAnyQuantity && onGoToCart) {
                                onGoToCart();
                            } else {
                                onClose({}, 'escapeKeyDown');
                            }
                        }}
                        className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors ${hasAnyQuantity
                            ? 'bg-green-500 border border-green-600 text-white hover:bg-green-600'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                    >
                        {hasAnyQuantity ? t('CONTINUE_TO_CHECKOUT') : t('CLOSE_MODAL')}
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default RecommendedFoodDialog;
